import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ScreenNetworkSpeed, ScreenResourceSample, formatBytes } from './Screens.types';

const cpuColor = '#1473e6';
const ramColor = '#2e7d32';
const netColor = '#7b1fa2';
const diskUsedColor = '#ed6c02';
const diskFreeColor = '#90a4ae';

const formatRate = (bytesPerSec: number) => {
    const mbps = (bytesPerSec * 8) / 1_000_000;
    if (mbps >= 1) return `${mbps.toFixed(1)} Mbps`;
    const kbps = (bytesPerSec * 8) / 1000;
    if (kbps >= 1) return `${kbps.toFixed(0)} Kbps`;
    return `${Math.round(bytesPerSec)} B/s`;
};

const clock = (iso: string) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const sameSamples = (left: ScreenResourceSample[], right: ScreenResourceSample[]) => {
    if (left.length !== right.length) return false;
    if (left.length === 0) return true;
    const a = left[left.length - 1];
    const b = right[right.length - 1];
    return a.at === b.at && a.cpuPercent === b.cpuPercent && a.memUsedBytes === b.memUsedBytes && a.diskUsedBytes === b.diskUsedBytes && a.netBytesPerSec === b.netBytesPerSec;
};

/** CPU, memory and network lines, plus a disk donut. New samples extend the existing charts. */
export const ScreenResourcePanel: React.FC<{ samples: ScreenResourceSample[]; networkSpeed?: ScreenNetworkSpeed }> = React.memo(({ samples, networkSpeed }) => {
    const points = useMemo(
        () => samples.map((sample) => ({
            time: clock(sample.at),
            cpu: Math.round(sample.cpuPercent * 10) / 10,
            ram: sample.memTotalBytes > 0 ? Math.round((sample.memUsedBytes / sample.memTotalBytes) * 1000) / 10 : 0,
            net: Math.round(((sample.netBytesPerSec || 0) * 8) / 100) / 10,
        })),
        [samples],
    );
    const latest = samples[samples.length - 1];
    const diskUsed = latest?.diskUsedBytes ?? 0;
    const diskFree = Math.max(0, (latest?.diskTotalBytes ?? 0) - diskUsed);
    const disk = useMemo(
        () => [
            { name: 'Used', value: diskUsed },
            { name: 'Free', value: diskFree },
        ],
        [diskUsed, diskFree],
    );

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Resources
            </Typography>
            {points.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    Waiting for the screen to report CPU, memory, disk and network.
                </Typography>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr 1fr 240px' }, gap: 2, alignItems: 'center' }}>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: cpuColor }}>
                            CPU {latest ? `${latest.cpuPercent.toFixed(1)}%` : ''}
                        </Typography>
                        <ResponsiveContainer width="100%" height={180}>
                            <LineChart data={points}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="time" minTickGap={24} tick={{ fontSize: 11 }} />
                                <YAxis domain={[0, 100]} unit="%" width={40} tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Line type="monotone" dataKey="cpu" name="CPU" stroke={cpuColor} dot={false} strokeWidth={2} isAnimationActive={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: ramColor }}>
                            Memory {latest && latest.memTotalBytes > 0 ? `${formatBytes(latest.memUsedBytes)} of ${formatBytes(latest.memTotalBytes)}` : ''}
                        </Typography>
                        <ResponsiveContainer width="100%" height={180}>
                            <LineChart data={points}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="time" minTickGap={24} tick={{ fontSize: 11 }} />
                                <YAxis domain={[0, 100]} unit="%" width={40} tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Line type="monotone" dataKey="ram" name="Memory" stroke={ramColor} dot={false} strokeWidth={2} isAnimationActive={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: netColor }}>
                            Network {latest ? formatRate(latest.netBytesPerSec || 0) : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {networkSpeed?.bytesPerSec
                                ? `Speed ${formatRate(networkSpeed.bytesPerSec)} · ${networkSpeed.at ? new Date(networkSpeed.at).toLocaleString() : ''}`
                                : 'Speed not measured yet'}
                        </Typography>
                        <ResponsiveContainer width="100%" height={180}>
                            <LineChart data={points}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="time" minTickGap={24} tick={{ fontSize: 11 }} />
                                <YAxis unit=" Kbps" width={56} tick={{ fontSize: 11 }} />
                                <Tooltip formatter={(value) => `${value} Kbps`} />
                                <Line type="monotone" dataKey="net" name="Network" stroke={netColor} dot={false} strokeWidth={2} isAnimationActive={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Disk {latest && latest.diskTotalBytes > 0 ? `${formatBytes(diskUsed)} of ${formatBytes(latest.diskTotalBytes)}` : ''}
                        </Typography>
                        <ResponsiveContainer width="100%" height={180}>
                            <PieChart>
                                <Pie data={disk} dataKey="value" nameKey="name" innerRadius={48} outerRadius={72} paddingAngle={2} isAnimationActive={false}>
                                    <Cell fill={diskUsedColor} />
                                    <Cell fill={diskFreeColor} />
                                </Pie>
                                <Tooltip formatter={(value) => formatBytes(Number(value) || 0)} />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>
                </Box>
            )}
        </Paper>
    );
}, (prev, next) => sameSamples(prev.samples, next.samples) && prev.networkSpeed?.at === next.networkSpeed?.at && prev.networkSpeed?.bytesPerSec === next.networkSpeed?.bytesPerSec);
